import React, { useState, useEffect, useRef, useMemo } from "react";
import imgs from "../../../../../../assets/images/imgs";
import { useAuth } from "../../../../../../features/auth/AuthContext";
import { useNavigate } from "react-router";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { proceedToPayData } from "../../../../../../state/actions/coursePaymentAction";
import { connectGoogleCalendar, disconnectGoogleCalendar } from "../../../../../../components/BookAMeeting/googleCalendar";
import { getTeacherOptions, getBookingSlots } from "../../../../../../components/BookAMeeting/meetingService";
import { Close } from "@mui/icons-material";
import CustomDropdown from "./CustomDropdown";
import CustomCalendar from "./CustomCalendar";
import BackButton from "../../../../../../components/BackButton/BackButton";
import ScheduleSuccess from "../../../../../../components/BookAMeeting/ScheduleSuccess";
import {google_calendar} from "../../../../../../assets/images/images"
const BookingModal = ({ isOpen, onClose, teacher, preFilledData, globalState }) => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Redux selectors for location
  const location = useSelector((state) => state?.globalState?.student_details?.data?.location);
  const getLocation = location ? location.split(",").pop().trim() : "India";
  const isIndia = getLocation === "India";

  const [bookingStep, setBookingStep] = useState(1); // 1: Form, 2: Summary
  const [bookingData, setBookingData] = useState({
    course: "",
    sessionType: "one-on-one",
    sessionSlot: "",
    meetingDate: "",
    meetingTime: "",
    meetingStart: "", // ISO string with timezone offset from API
  });
  const [calendarOpen, setCalendarOpen] = useState(false);
  const meetingDateBtnRef = useRef(null);

  const [gcalConnected, setGcalConnected] = useState(false);
  const [gcalAccount, setGcalAccount] = useState("");
  const [gcalBusy, setGcalBusy] = useState(false);
  const [gcalError, setGcalError] = useState("");

  const [teacherOptions, setTeacherOptions] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [disabledDates, setDisabledDates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [shakeMeetingDate, setShakeMeetingDate] = useState(false);
  const [shakeSessionSlot, setShakeSessionSlot] = useState(false);


  // Payment state
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [successPopup, setSuccessPopup] = useState(null);

  useEffect(() => {
    if (isOpen && token) {
      if (teacher?.teacher_id) {
        fetchTeacherOptions();
      } else {
        console.warn("⚠️ Teacher ID not available:", teacher);
      }
    }
  }, [isOpen, token, teacher?.teacher_id]);

  // Auto-fill session slot and session type when capsule is clicked
  useEffect(() => {
    if (preFilledData && isOpen) {
      setBookingData((prev) => ({
        ...prev,
        sessionSlot: preFilledData.sessionSlot,
        sessionType: preFilledData.sessionType,
      }));
    }
  }, [preFilledData, isOpen]);

  // Auto-dismiss Google Calendar error after 3 seconds
  useEffect(() => {
    if (!gcalError) return;
    const timer = setTimeout(() => setGcalError(""), 3000);
    return () => clearTimeout(timer);
  }, [gcalError]);

  const formatDateString = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const generateAllDatesInRange = (startDate, endDate) => {
    const dates = [];
    for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
      dates.push(formatDateString(new Date(d)));
    }
    return dates;
  };

  const fetchTeacherOptions = async () => {
    setLoading(true);
    try {
      const res = await getTeacherOptions(token, teacher.teacher_id);
      if (res.ok && res.data) {
        setTeacherOptions(res.data);
        // Set Google Calendar connection status from API response
        const hasGoogleCredential = res.data?.google_account?.name ?? false;
        console.log(hasGoogleCredential)
        setGcalConnected(hasGoogleCredential);
        setGcalAccount(hasGoogleCredential);

        // Calculate disabled dates
        const allDates = generateAllDatesInRange(
          new Date(),
          new Date(Date.now() + res.data.max_advance_days * 24 * 60 * 60 * 1000)
        );
        const availableDates = res.data.available_dates || [];
        const disabled = allDates.filter(date => !availableDates.includes(date));
        setDisabledDates(disabled);
      }
    } catch (error) {
      console.error("❌ Failed to fetch teacher options:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableSlots = async (date, sessionSlot) => {
    if (teacherOptions && date && sessionSlot) {
      setLoading(true);
      try {
        const res = await getBookingSlots(token, {
          teacherId: teacher.teacher_id,
          date: date,
          sessionSlot: sessionSlot,
          timezone: teacherOptions.timezone,
        });
        if (res.ok && res.data && res.data.days && res.data.days.length > 0) {
          setAvailableSlots(res.data.days[0].slots || []);
        } else if (res.ok && res.data && Array.isArray(res.data)) {
          setAvailableSlots(res.data);
        }
      } catch (error) {
        console.error("Failed to fetch slots:", error);
        setAvailableSlots([]);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleDateChange = async (date) => {
    setBookingData({ ...bookingData, meetingDate: date, meetingTime: "", meetingStart: "" });
    setCalendarOpen(false);
    await fetchAvailableSlots(date, bookingData.sessionSlot);
  };

  const handleSessionSlotChange = async (val) => {
    setBookingData({ ...bookingData, sessionSlot: val, meetingTime: "", meetingStart: "" });
    await fetchAvailableSlots(bookingData.meetingDate, val);
  };

  const handleGcalConnect = async () => {
    if (gcalBusy) return;
    setGcalBusy(true);
    setGcalError("");
    try {
      const res = await connectGoogleCalendar(token);
      if (res.ok) {
        setGcalConnected(true);
        // Extract account name from response if available
        const accountName = res.data?.data?.google_account.name || res.data?.data?.google_account.email || "Connected";
        setGcalAccount(accountName);
      } else {
        setGcalError(res.error || "Could not connect Google Calendar.");
      }
    } catch (e) {
      // Only show error for actual failures, not for popup closure
      const isPopupClosed = /popup|closed|denied|access_denied/i.test(e?.message || "");
      if (!isPopupClosed) {
        setGcalError(e?.message || "Could not connect Google Calendar.");
      }
    } finally {
      setGcalBusy(false);
    }
  };

  const handleGcalDisconnect = async () => {
    if (gcalBusy) return;
    setGcalBusy(true);
    setGcalError("");
    const res = await disconnectGoogleCalendar(token);
    setGcalBusy(false);
    if (res.ok) {
      setGcalConnected(false);
      setGcalAccount("");
    } else {
      setGcalError(res.error || "Could not disconnect.");
    }
  };

  const handleClose = () => {
    onClose();
    setBookingStep(1);
    setBookingData({
      course: "",
      sessionType: "one-on-one",
      sessionSlot: "",
      meetingDate: "",
      meetingTime: "",
      meetingStart: "",
    });
  };

  const getOfferingBySlot = (sessionSlot) => {
    if (teacherOptions?.offerings && sessionSlot) {
      // Extract duration from sessionSlot (e.g., "30min" -> 30)
      const durationMatch = sessionSlot.match(/(\d+)min/);
      if (durationMatch) {
        const duration = parseInt(durationMatch[1]);
        return teacherOptions.offerings.find((o) => o.duration_minutes === duration);
      }
    }
    return null;
  };

  const getSessionSlotOptions = () => {
    // Dynamically generate session slot options from teacher offerings
    if (loading) {
      return [{ id: "", label: "Loading...", disabled: true }];
    }
    if (teacherOptions?.offerings && teacherOptions.offerings.length > 0) {
      return teacherOptions.offerings.map((offering) => ({
        id: `${offering.duration_minutes}min`,
        label: `${offering.duration_minutes} Min`,
      }));
    }
    // No offerings available
    return [{ id: "", label: "No sessions", disabled: true }];
  };

  const getCurrencySymbol = () => {
    // Get currency from teacherOptions, then fallback to teacher details or default
    return teacherOptions?.currency_symbol ||
           globalState?.teacher_details?.data?.currency_symbol ||
           "$";
  };

  const getSessionPrice = () => {
    // Always get the actual offering price from the backend
    // Payment requirement is handled by the bookings API
    const offering = getOfferingBySlot(bookingData.sessionSlot);
    return offering ? parseFloat(offering.price) : 0;
  };

  const formatTimeForPayload = (timeString) => {
    if (!timeString) return "";
    // Extract only HH:mm:ss if full ISO datetime is provided
    if (timeString.includes('T')) {
      return timeString.split('T')[1].split('+')[0]; // Extract HH:mm:ss from ISO datetime
    }
    return timeString;
  };

  const to12HourFormat = (time24) => {
    if (!time24) return "";
    const [hours, minutes] = time24.split(":").map(Number);
    const ampm = hours >= 12 ? "PM" : "AM";
    const hours12 = hours % 12 || 12;
    return `${hours12}:${String(minutes).padStart(2, "0")} ${ampm}`;
  };

  // Derive offeringId from sessionSlot and compute booking payload
  const bookingPayloadData = useMemo(
    () => {
      const offering = getOfferingBySlot(bookingData.sessionSlot);
      return {
      teacherId: teacher.teacher_id,
      course: bookingData.course,
      sessionType: bookingData.sessionType,
      sessionSlot: bookingData.sessionSlot,
      offeringId: offering?.id || null,
      meetingDate: bookingData.meetingDate,
      meetingTime: formatTimeForPayload(bookingData.meetingTime),
      start: bookingData.meetingStart, // ISO string with timezone offset
      };
    },
    [teacher.teacher_id, bookingData, teacherOptions]
  );

  const handleDirectBooking = async () => {
    // Google Calendar must be connected before booking
    if (!gcalConnected) {
      setGcalError("Connect Google Calendar to schedule a meeting.");
      toast.error("Please connect your Google Calendar before scheduling a meeting.");
      return;
    }

    try {
      setCheckoutLoading(true);
      const { createMeetingBooking } = await import("../../../../../../components/BookAMeeting/meetingService");

      const bookingPayload = {
        teacher_id: teacher.teacher_id,
        course_id: bookingData.course || null,
        offering_id: bookingPayloadData.offeringId,
        start: bookingPayloadData.start,
      };

      const res = await createMeetingBooking(token, bookingPayload);
      if (res.ok) {
        // Show success popup with meeting details
        setSuccessPopup({
          date: bookingData.meetingDate,
          time: bookingData.meetingTime,
          isTrial: teacherOptions?.is_trial,
        });

        // Close modal and navigate after popup closes
        setTimeout(() => {
          handleClose();
          navigate("/dashboard/meetings");
        }, 4000);
      } else {
        toast.error(res.error || "Failed to schedule meeting");
      }
    } catch (error) {
      console.error("Error booking meeting:", error);
      toast.error("Failed to schedule meeting");
    } finally {
      setCheckoutLoading(false);
    }
  };


  const handleProceedToCheckout = async () => {
    // Google Calendar must be connected before booking
    if (!gcalConnected) {
      setGcalError("Connect Google Calendar to schedule a meeting.");
      toast.error("Please connect your Google Calendar before scheduling a meeting.");
      return;
    }

    // Session details must be loaded before proceeding
    if (!bookingPayloadData.offeringId) {
      toast.error("Session details are still loading. Please try again.");
      return;
    }

    // Check if marketplace is enabled and if free trial is available
    const isPaidMeetingEnabled = globalState?.enabled_modules?.includes("marketplace");
    const isFreeTrialAvailable = teacherOptions?.is_trial;

    // If marketplace disabled OR free trial available - directly book the meeting
    if (isPaidMeetingEnabled === false || isFreeTrialAvailable) {
      handleDirectBooking();
      return;
    }

    // Marketplace enabled and not free trial - show booking summary first
    setBookingStep(2);
  };

  const handleCheckoutFromSummary = async () => {
    // Check if marketplace is enabled for this teacher
    const isPaidMeetingEnabled = globalState?.enabled_modules?.includes("marketplace");
    const isFreeTrialAvailable = teacherOptions?.is_trial;
    // If marketplace disabled OR free trial available - directly book the meeting (no payment)
    if (isPaidMeetingEnabled === false || isFreeTrialAvailable) {
      handleDirectBooking();
      return;
    }

    // Payment required - create booking first (for meet_session)
    setCheckoutLoading(true);
    const result = await dispatch(proceedToPayData(token, null, false, 0, "meet_session", bookingPayloadData));
      setCheckoutLoading(false);

    // Check result and navigate only if booking succeeded
    if (!result?.ok) {
      toast.error(result?.error || "Failed to process booking");
      return;
    }

      navigate("/BookingCheckout", {
        state: {
          purchaseType: "meet_session",
          bookingData: {
            ...bookingPayloadData,
            meetingTime: formatTimeForPayload(bookingPayloadData.meetingTime),
          },
          teacherOptions: teacherOptions,
          orderId: result.data?.payment?.order_id,
          amount: result.data?.payment?.amount,
        },
      });
  };

  if (!isOpen) return null;

  const GCalLogo = () => (
    <svg width="30" height="30" viewBox="0 0 40 40" aria-hidden="true">
      <rect x="6" y="9" width="28" height="25" rx="4" fill="#fff" stroke="#e3e6ec" />
      <rect x="6" y="9" width="28" height="7" rx="4" fill="#4285F4" />
      <text
        x="20"
        y="30"
        textAnchor="middle"
        fontSize="13"
        fontWeight="700"
        fill="#4285F4"
        fontFamily="Arial, sans-serif"
      >
        31
      </text>
    </svg>
  );
  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 sm:p-6"
      onClick={handleClose}
    >
      <div
        className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 md:p-8 w-full max-w-sm sm:max-w-md md:max-w-2xl max-h-[90vh] overflow-y-auto overflow-x-visible relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-2xl transition-colors bg-transparent border-none"
        >
          <Close/>
        </button>

        {/* STEP 1: BOOKING FORM */}
        {bookingStep === 1 && (
          <>
            <h3 className="text-base sm:text-lg font-bold mb-3 sm:mb-4 mt-0">Book A Meeting</h3>

            {/* Google Calendar Section */}
            <div className="mb-6 border-b border-gray-200">
              <div className="bm-integ-card">
                <div className="bm-integ-main">
                  <div className="bm-integ-logo">
                    <img src={google_calendar} alt="google_calendar"/>
                  </div>
                  <div className="bm-integ-info">
                    <p className="font-semibold text-gray-900 text-xs sm:text-sm">Google Calendar</p>
                    <p className="text-xs text-gray-600">
                      <span
                        className={`inline-block w-2 h-2 rounded-full mr-2 ${
                          gcalConnected ? "bg-green-500" : "bg-gray-400"
                        }`}
                      />
                      {gcalConnected ? `connected as ${gcalAccount || "Connected"}` : "Not Connected"}
                    </p>
                  </div>
                </div>
                <div className="bm-integ-action">
                {gcalConnected ? (
                  <button
                    onClick={handleGcalDisconnect}
                    disabled={gcalBusy}
                    className="bm-outline-btn"
                  >
                    {gcalBusy ? "Disconnecting…" : "Disconnect"}
                  </button>
                ) : (
                  <button
                    onClick={handleGcalConnect}
                    disabled={gcalBusy}
                    className="bm-outline-btn"
                  >
                    {gcalBusy ? "Connecting…" : "Connect"}
                  </button>
                )}
                </div>
              </div>
              {gcalError && <p className="text-xs text-red-600 mt-2">{gcalError}</p>}
            </div>

            {/* Form Grid Layout - First Row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4 mb-4 sm:mb-6">
              {/* Teacher Column */}
              <div>
                <label className="block text-xs sm:text-sm font-semibold text-gray-600 mb-1 sm:mb-2">
                  Teacher
                </label>
                <div className="flex items-center gap-3 rounded-lg">
                  <img
                    src={teacher?.ai_avatar || imgs.png.ab_img}
                    alt={teacher?.name}
                    className="w-8 h-8 rounded-full object-cover flex-shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate">
                      {teacher?.name}
                    </p>         
                  </div>
                  <div className="studentDashboar-Suggested-Courses-profile-rate !flex-row">
                    <a><i className="fa-solid fa-star"></i></a>
                    <p>{teacher?.average_teacher_rating}</p>
                  </div>
                </div>
              </div>

              {/* Course Column */}
              <CustomDropdown
                label="Course"
                value={bookingData.course}
                placeholder="Select Course"
                onChange={(val) =>
                  setBookingData({
                    ...bookingData,
                    course: val,
                  })
                }
                options={
                  loading
                    ? [{ id: "", label: "Loading courses...", disabled: true }]
                    : teacherOptions?.courses && teacherOptions.courses.length > 0
                    ? teacherOptions.courses.map((course) => ({
                        id: course.course_id,
                        label: course.course_name,
                      }))
                    : [{ id: "", label: "No courses available", disabled: true }]
                }
              />

              {/* Session Type Column */}
              <CustomDropdown
                label={
                  <>
                    Session Type <span className="text-red-500">*</span>
                  </>
                }
                value={bookingData.sessionType}
                placeholder="Select Session Type"
                onChange={(val) =>
                  setBookingData({
                    ...bookingData,
                    sessionType: val,
                  })
                }
                options={[
                  {
                    id: "one-on-one",
                    label: "1 on 1 Session",
                  },
                ]}
              />
            </div>

            {/* Form Grid Layout - Second Row */}
            {/* Form Grid Layout - Second Row */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4 mb-4 sm:mb-6">
            
            {/* Session Slot Column */}
            {/* Session Slot Column */}
            <div className={shakeSessionSlot ? "animate-shake ring-2 ring-red-500 rounded-lg" : ""}>
              <CustomDropdown
                label={
                  <>
                    Session Slot <span className="text-red-500">*</span>
                  </>
                }
                value={bookingData.sessionSlot}
                placeholder="Select Session Slot"
                onChange={handleSessionSlotChange}
                options={getSessionSlotOptions()}
              />
            </div>

              {/* Meeting Date Column */}
              {/* Added conditional shake class to the parent wrapper of Meeting Date */}
              <div className={`relative ${shakeMeetingDate ? "animate-shake ring-2 ring-red-500 rounded-lg" : ""}`}>
                <label className="block text-xs sm:text-sm font-semibold text-gray-600 mb-1 sm:mb-2">
                  Meeting Date <span className="text-red-500">*</span>
                </label>
              
              <div 
                onClick={() => {
                  if (!bookingData.sessionSlot) {
                    setShakeSessionSlot(true);
                    setTimeout(() => setShakeSessionSlot(false), 300);
                  }
                }}
                className={!bookingData.sessionSlot ? "cursor-not-allowed" : ""}
              >
                <button
                  ref={meetingDateBtnRef}
                  type="button"
                  onClick={() => {
                    if (bookingData.sessionSlot) {
                      setCalendarOpen(!calendarOpen);
                    }
                  }}
                  disabled={!bookingData.sessionSlot}
                  className={`w-full px-2 sm:px-3 py-2 border border-gray-300 rounded-lg text-sm text-left text-gray-900 bg-white flex items-center justify-between transition-colors ${
                    bookingData.sessionSlot
                      ? "hover:border-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent cursor-pointer"
                      : "pointer-events-none opacity-50 bg-gray-100"
                  }`}
                >
                  <span className={bookingData.meetingDate ? "text-gray-900" : "text-gray-500"}>
                    {bookingData.meetingDate || "Select Date"}
                  </span>
                  <i className="fa-solid fa-calendar text-gray-400"></i>
                </button>
            </div>
                {!bookingData.sessionSlot && (
                  <p className="text-xs text-[#858C93] mb-2 mt-1">pick a session slot first</p>
                )}
                <CustomCalendar
                  value={bookingData.meetingDate}
                  onChange={handleDateChange}
                  disabledDates={disabledDates}
                  triggerRef={meetingDateBtnRef}
                  isOpen={calendarOpen}
                  onClose={() => setCalendarOpen(false)}
                />
              </div>

              {/* Meeting Time Column */}
              <div>
                <label className="block text-xs sm:text-sm font-semibold text-gray-600 mb-1 sm:mb-2">
                  Meeting Time <span className="text-red-500">*</span>
                </label>
              
              {/* Intercepts click events when disabled to shake the Meeting Date container */}
              <div
                onClick={() => {
                  if (!bookingData.meetingDate) {
                    setShakeMeetingDate(true);
                    setTimeout(() => setShakeMeetingDate(false), 300);
                  }
                }}
                className={!bookingData.meetingDate ? "cursor-not-allowed" : ""}
              >
                <div className={!bookingData.meetingDate ? "pointer-events-none" : ""}>
                <CustomDropdown
                  value={bookingData.meetingTime}
                  placeholder={
                    !bookingData.meetingDate
                      ? "Select Time"
                      : loading
                      ? "Loading times..."
                      : "Select Time"
                  }
                  onChange={(slotLocalTime) => {
                    const selectedSlot = availableSlots.find((s) => s.local_time === slotLocalTime);
                    if (bookingData.meetingDate && selectedSlot) {
                      setBookingData({
                        ...bookingData,
                        meetingTime: selectedSlot.local_time,
                        meetingStart: selectedSlot.start,
                      });
                    }
                  }}
                  disabled={!bookingData.meetingDate || loading}
                  options={
                    !bookingData.meetingDate
                      ? []
                      : loading
                      ? [{ id: "", label: "Loading...", disabled: true }]
                      : availableSlots && availableSlots.length > 0
                      ? availableSlots.map((slot) => ({
                          id: slot.local_time,
                          label: to12HourFormat(slot.local_time),
                        }))
                      : [{ id: "", label: "No slots available", disabled: true }]
                  }
                />
              </div>
              </div>
              
              {!bookingData.meetingDate && (
                <p className="text-xs text-[#858C93] mb-2 mt-1">pick a meeting date</p>
              )}
            </div>

          </div>

            {/* Action Button - Centered */}
            <div className="flex justify-end">
              <button
                onClick={handleProceedToCheckout}
                disabled={
                  !bookingData.sessionType ||
                  !bookingData.sessionSlot ||
                  !bookingData.meetingDate ||
                  !bookingData.meetingTime ||
                  checkoutLoading
                }
                className="w-full sm:w-auto px-4 sm:px-6 py-2 sm:py-2.5 text-sm sm:text-base rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-[#B3B3B3] text-white hover:bg-blue-700 enabled:bg-blue-700 enabled:hover:bg-blue-800 disabled:hover:bg-blue-600"
              >
                {checkoutLoading ? "Processing..." : "Schedule Meeting"}
              </button>
            </div>
          </>
        )}

        {/* STEP 2: BOOKING SUMMARY - Always shown before checkout or direct booking */}
        {bookingStep === 2 && (
          <>
            <h3 className="text-base sm:text-lg font-bold mb-3 sm:mb-4 mt-0">Booking Summary</h3>
            <h4 className="text-md font-bold mb-6 mt-0">Billing Summary</h4>

            {/* Booking Details */}
            <div className="pb-3 sm:pb-4 pt-3 sm:pt-4 border-b border-gray-200 flex flex-col sm:flex-row justify-between gap-2 sm:gap-0">
              <div className="space-y-0.5 sm:space-y-1">
                <div className="flex gap-1 sm:gap-2 flex-wrap">
                  <p className="text-xs text-gray-600">Teacher:</p>
                  <p className="font-semibold text-gray-900 text-xs sm:text-sm">
                    {teacher?.name}
                  </p>
                </div>
                <div className="flex gap-1 sm:gap-2 flex-wrap">
                  <p className="text-xs text-gray-600">Course:</p>
                  <p className="font-semibold text-gray-900 text-xs sm:text-sm">
                    {teacherOptions?.courses?.find(c => c.course_id === bookingData.course)?.course_name || "-"}
                  </p>
                </div>
                <div className="flex gap-1 sm:gap-2 flex-wrap">
                  <p className="text-xs text-gray-600">Session Type:</p>
                  <p className="font-semibold text-gray-900 text-xs sm:text-sm">
                    {bookingData.sessionType === "one-on-one" ? "One on One Session" : "Group Session"}
                  </p>
                </div>
                <div className="flex gap-1 sm:gap-2 flex-wrap">
                  <p className="text-xs text-gray-600">Schedule:</p>
                  <p className="font-semibold text-gray-900 text-xs sm:text-sm">
                    {bookingData.meetingDate}, {to12HourFormat(bookingData.meetingTime)}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-gray-600 text-xs sm:text-sm">{getCurrencySymbol()}{getSessionPrice().toFixed(2)}</p>
              </div>
            </div>

              <div className="pt-2 sm:pt-3 pb-2 sm:pb-3 flex justify-between font-semibold text-sm sm:text-base" style={{borderTop:"1px solid #d9d9d9"}}>
                  <span className="text-gray-900">Total:</span>
                  <span className="text-blue-600">{getCurrencySymbol()}{getSessionPrice().toFixed(2)}</span>
              </div>      

            {/* Action Buttons */}
            <div className="flex gap-3 justify-between">
                <BackButton buttonName={"Back"} onClick={() => setBookingStep(1)}/>
              <button
                onClick={handleCheckoutFromSummary}
                disabled={checkoutLoading}
                style={{
                  backgroundColor: !checkoutLoading ? "#0068FF" : "gray",
                  color: !checkoutLoading ? "#ffffff" : "colorWhenDisabled",
                }}
                className="w-full sm:w-auto px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 border-none disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {checkoutLoading ? "Processing..." : "Proceed to Checkout"}
              </button>
            </div>

          </>
        )}
      </div>

      {/* Success popup after booking */}
      {successPopup && (
        <ScheduleSuccess
          meeting={successPopup}
          onClose={() => setSuccessPopup(null)}
        />
      )}
    </div>
  );
};

export default BookingModal;
